/************************************************************
 * Explanation:
 * - allDesigns: an array of objects, each representing
 *   one generated design. Each object has:
 *       {
 *         thumbnail: <base64 of currently selected image>,
 *         images: <array of 4 base64 images>,
 *         activeImageIndex: <which of the 4 images is selected>,
 *         selected: <boolean indicating if the design is selected>
 *       }
 * - activeDesignIndex: which design in the left column
 *   is currently being viewed in the right column.
 ************************************************************/

let allDesigns = [];
let activeDesignIndex = -1; // no active design initially

/**
 * Creates a new design by calling your Azure /generate endpoint.
 * Then stores a single thumbnail in the left column and
 * sets the right column to show all four images.
 */
async function createDesign() {
  if (!checkRateLimit()) {
    alert("You have reached the generation limit for today. Please try again tomorrow.");
    return;
  }
  
  const userInput = document.getElementById("design-input").value.trim();
  if (!userInput) {
    alert("Please enter a design description.");
    return;
  }
  
  const createBtn = document.getElementById("create-design-btn");
  createBtn.disabled = true;
  createBtn.innerText = "Generating Design...";
  
  try {
    // Call your Azure Container Apps endpoint
    const response = await fetch("https://modernaca.icyforest-4723523e.westus2.azurecontainerapps.io/generate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        prompt: "Create a black and white vector design enclosed in a circle based on this discription:" + userInput,
        model: "V_2A_TURBO",
        magic_prompt_option: "OFF"
      })
    });
    
    const data = await response.json();
    
    createBtn.disabled = false;
    createBtn.innerText = "Create Design";
    
    // Check for returned images
    if (!data.composite_images || data.composite_images.length === 0) {
      alert("Error generating design. Please try again.");
      return;
    }
    
    // Create a design object to store
    // Start with the first image as the thumbnail
    const newDesign = {
      thumbnail: data.composite_images[0],
      images: data.composite_images,
      overlayImage: data.overlay_image,
      activeImageIndex: 0,  // index of the selected image among the 4
      selected: false       // not selected by default
    };
    
    // Push it to the global allDesigns array
    allDesigns.push(newDesign);
    
    // The newly created design becomes the active one
    activeDesignIndex = allDesigns.length - 1;
    
    // Re-render the left column thumbnails
    renderLeftThumbnails();
    
    // Load the newly created design on the right
    loadDesign(activeDesignIndex);
    updateDesignerModalButton();

    // Save state
    //saveState();

  } catch (error) {
    createBtn.disabled = false;
    createBtn.innerText = "Create Design";
    console.error("Error:", error);
    alert("Failed to create design.");
  }
}

/**
 * Renders one thumbnail for each design in the left column.
 * - Blue border around the active design's thumbnail
 * - A small selectable circle in the top-right corner
 *   to indicate "selected" for sending.
 */
function renderLeftThumbnails() {
  const designHistory = document.getElementById("design-history");
  designHistory.innerHTML = ""; // clear old thumbnails

  allDesigns.forEach((design, index) => {
    // Create a wrapper so we can position the circle absolutely
    const wrapper = document.createElement("div");
    wrapper.classList.add("thumbnail-wrapper");
    
    // The actual thumbnail image
    const thumb = document.createElement("img");
    thumb.src = "data:image/png;base64," + design.thumbnail;
    thumb.classList.add("history-thumbnail");
    
    // If this is the active design, give it a blue border
    if (index === activeDesignIndex) {
      thumb.classList.add("selected-thumbnail");
    } else {
      thumb.classList.remove("selected-thumbnail");
    }
    
    // Clicking this thumbnail makes it the active design
    thumb.onclick = () => {
      activeDesignIndex = index;
      renderLeftThumbnails(); // re-render so we highlight the correct thumbnail
      loadDesign(index);
    };

    // Create the small circle overlay
    const selectCircle = document.createElement("div");
    selectCircle.classList.add("select-circle");
    // If the design is selected, fill the circle with blue
    if (design.selected) {
      selectCircle.classList.add("selected-circle");
    }
    
    // Clicking the circle toggles the selected state
    selectCircle.onclick = (e) => {
      e.stopPropagation(); // prevent triggering the thumb's onclick
      design.selected = !design.selected;
      // Re-render the left column to update the circle color
      renderLeftThumbnails();
      // Update send to designer button
      updateDesignerModalButton();
    };
    
    // Add the elements to the wrapper
    wrapper.appendChild(thumb);
    wrapper.appendChild(selectCircle);
    
    // Add the wrapper to the designHistory container
    designHistory.appendChild(wrapper);
  });
}

async function updateDesignerModalButton() {
  const maxSelectedDesigns = 10;

  const numSelectedDesigns = allDesigns.filter(d => d.selected).length;
  const sendBtn = document.getElementById("send-design-btn");

  if (numSelectedDesigns == 0) {
    sendBtn.disabled = true;
    sendBtn.innerText = "No Designs Selected";
  } else if (numSelectedDesigns <= maxSelectedDesigns) {
    sendBtn.disabled = false;
    sendBtn.innerText = "Send Selected to Designer";
  } else {
    sendBtn.disabled = true;
    sendBtn.innerText = "Too Many Designs Selected";
  }
}

/**
 * Loads a specific design's 4 images into the right column:
 * - Large preview set to the design's currently selected image
 * - Thumbnails (4) displayed below it, with the selected one highlighted
 */
function loadDesign(designIndex) {
  const largePreview = document.getElementById("large-preview");
  const fourImagesContainer = document.getElementById("four-images-container");

  // Clear the container before rendering
  fourImagesContainer.innerHTML = "";

  // If no design exists, load default preview images
  if (designIndex < 0 || !allDesigns[designIndex]) {
    // Define 4 distinct default preview URLs
    const defaultPreviewUrls = [
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Backgroun1Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background2Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background3Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background4Custom.png?v=1741114659"
    ];
    
    // Set the large preview to the first default image
    largePreview.src = defaultPreviewUrls[0];
    
    // Create an image element for each default URL
    defaultPreviewUrls.forEach(url => {
      const img = document.createElement("img");
      img.src = url;
      img.classList.add("four-image");
      fourImagesContainer.appendChild(img);
    });
    
    return;
  }

  // Otherwise, load the selected design's images
  const design = allDesigns[designIndex];
  largePreview.src = "data:image/png;base64," + design.images[design.activeImageIndex];

  design.images.forEach((base64Image, i) => {
    const img = document.createElement("img");
    img.src = "data:image/png;base64," + base64Image;
    img.classList.add("four-image");

    // Highlight the active preview image
    if (i === design.activeImageIndex) {
      img.classList.add("selected-thumbnail");
    }

    // Update the active image and thumbnail when a preview is clicked
    img.onclick = () => {
      design.activeImageIndex = i;
      design.thumbnail = base64Image;
      renderLeftThumbnails();
      loadDesign(designIndex);
    };

    fourImagesContainer.appendChild(img);
  });
}



/************************************************************
 * Modal-related code: sending selected designs to the server
 ************************************************************/

/**
 * Opens the modal for email + message submission.
 * But first checks if at least one design is selected.
 */
function openDesignerModal() {
  // Check if at least one design has design.selected = true
  const hasSelected = allDesigns.some(d => d.selected);
  if (!hasSelected) {
    alert("Please select at least one design before submitting.");
    return;
  }
  document.getElementById("designer-modal").style.display = "flex";
}

function openSuccessModal() {
  document.getElementById("success-modal").style.display = "flex";
}

/**
 * Closes the modal
 */
function closeDesignerModal() {
  document.getElementById("designer-modal").style.display = "none";
}
function closeSuccessModal() {
  document.getElementById("success-modal").style.display = "none";
}

/**
 * Sends the selected designs (their thumbnails) to /send-design
 */
async function submitDesignerForm() {
  const email = document.getElementById("user-email").value.trim();
  const message = document.getElementById("user-message").value.trim();
  const coinAmount = document.getElementById("user-coin-amount").value;
  const coinMaterial = document.getElementById("user-coin-material").value;
  
  if (!email) {
    alert("Please enter your email.");
    return;
  }
  
  // Only send the thumbnail images of designs that are selected
  const selectedThumbnails = allDesigns
    .filter(d => d.selected)
    .map(d => d.thumbnail);

  const selectedOverlays = allDesigns
    .filter(d => d.selected)
    .map(d => d.overlayImage);
  
  const payload = {
    images: selectedThumbnails,
    overlays: selectedOverlays,
    email: email,
    message: message,
    coinAmount: coinAmount,
    coinMaterial: coinMaterial
  };

  // Show the loading indicator
  document.getElementById("submit-loading-indicator").style.display = "block";
  
  try {
    const response = await fetch("https://modernaca.icyforest-4723523e.westus2.azurecontainerapps.io/send-design", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
    
    const result = await response.json();
    
    if (result.success) {
      //alert("Design(s) sent to designer successfully!");
      
      // Clear out selected states
      allDesigns.forEach(d => d.selected = false);
      renderLeftThumbnails(); // update circles
      
      // Close modal
      document.getElementById("designer-modal").style.display = "none";
      // Reset large preview if you wish
      document.getElementById("large-preview").src = "https://via.placeholder.com/400";
    } else {
      //alert("Failed to send design(s).");
    }
  } catch (error) {
    console.error("Error:", error);
    //alert("Error sending design(s).");
  } finally {
    // Hide the loading indicator regardless of success or error
    document.getElementById("submit-loading-indicator").style.display = "none";
    loadDesign(activeDesignIndex);
    openSuccessModal();
  }
}

// Add this at the top of your JS file
let defaultActiveImageIndex = 0;

function loadDesign(designIndex) {
  const largePreview = document.getElementById("large-preview");
  const fourImagesContainer = document.getElementById("four-images-container");

  // Clear the container before rendering
  fourImagesContainer.innerHTML = "";

  // If no design exists, load default preview images
  if (designIndex < 0 || !allDesigns[designIndex]) {
    // Define 4 distinct default preview URLs
    const defaultPreviewUrls = [
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Backgroun1Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background2Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background3Custom.png?v=1741114658",
      "https://cdn.shopify.com/s/files/1/0738/1880/0445/files/Background4Custom.png?v=1741114659"
    ];
    
    // Set the large preview to the default image at defaultActiveImageIndex
    largePreview.src = defaultPreviewUrls[defaultActiveImageIndex];
    
    // Create an image element for each default URL
    defaultPreviewUrls.forEach((url, i) => {
      const img = document.createElement("img");
      img.src = url;
      img.classList.add("four-image");
      
      // Highlight the selected default image
      if (i === defaultActiveImageIndex) {
        img.classList.add("selected-thumbnail");
      }
      
      // When clicking a default image, update the active default index and reload the default view
      img.onclick = () => {
        defaultActiveImageIndex = i;
        loadDesign(-1); // pass -1 so that it uses the default branch
      };
      
      fourImagesContainer.appendChild(img);
    });

    //saveState();
    return;
  }

  // Otherwise, load the selected design's images
  const design = allDesigns[designIndex];
  largePreview.src = "data:image/png;base64," + design.images[design.activeImageIndex];

  design.images.forEach((base64Image, i) => {
    const img = document.createElement("img");
    img.src = "data:image/png;base64," + base64Image;
    img.classList.add("four-image");

    // Highlight the active preview image
    if (i === design.activeImageIndex) {
      img.classList.add("selected-thumbnail");
    }

    // When clicking a preview image, update the active image, update the thumbnail,
    // and re-render both the left thumbnails and the right preview.
    img.onclick = () => {
      design.activeImageIndex = i;
      design.thumbnail = base64Image;
      renderLeftThumbnails();
      loadDesign(designIndex);
    };

    fourImagesContainer.appendChild(img);
  });
}


function saveState() {
  localStorage.setItem("allDesigns", JSON.stringify(allDesigns));
  localStorage.setItem("activeDesignIndex", activeDesignIndex);
}

function loadState() {
  const storedDesigns = localStorage.getItem("allDesigns");
  if (storedDesigns) {
    allDesigns = JSON.parse(storedDesigns);
  } else {
    allDesigns = [];
  }
  const storedActiveIndex = localStorage.getItem("activeDesignIndex");
  if (storedActiveIndex !== null) {
    activeDesignIndex = parseInt(storedActiveIndex, 10);
  } else {
    activeDesignIndex = -1;
  }
}

function checkRateLimit() {
  // Get today's date as a string "YYYY-MM-DD"
  const today = new Date().toISOString().slice(0, 10);
  // Retrieve the rate limit object from localStorage
  const storedData = localStorage.getItem("generationRateLimit");
  let rateLimitData;
  
  if (storedData) {
    rateLimitData = JSON.parse(storedData);
    // If the stored date is not today, reset the count
    if (rateLimitData.date !== today) {
      rateLimitData = { date: today, count: 0 };
    }
  } else {
    rateLimitData = { date: today, count: 0 };
  }
  
  // If we've reached the limit of 40 generations for today, return false
  if (rateLimitData.count >= 40) {
    return false;
  }
  
  // Otherwise, increment the count and store it back in localStorage
  rateLimitData.count += 1;
  localStorage.setItem("generationRateLimit", JSON.stringify(rateLimitData));
  
  return true;
}




document.addEventListener("DOMContentLoaded", function () {
  loadState();
  updateDesignerModalButton();
  loadDesign(activeDesignIndex);
});
